import { defineStore } from "pinia";
import { BrpResponse } from "@/types";
import {
    canMovePlanMeasure,
    canMovePlanStep,
    getSelectedRecommendationsId,
    RoadmapStrategy,
    type BrpEcmKey,
    type BrpRouteKey
} from "@/helpers";
import { useRecommendations } from "@/store/recommendations";
import { useLoan } from "@/store/loan";
import { useSelections } from "@/store/selections";
import { BrpReportService } from "@/service/brp-report.service";
import type { RecommendationGroup } from "@/types/recommendations";

export interface RecommendationStepItem {
    title: string;
    comfort?: number;
    cost?: string | number;
    id?: string;
    recommendations: string[];
    recommendationGroupId?: string | null;
    description?: string;
}

export interface PassportStep {
    step: number;
    year: number | undefined;
    items: RecommendationStepItem[];
}

export interface PassportPlanMeasure {
    _key: string;
    name: string;
    description: string;
    ecmKey?: BrpEcmKey | null;
    routeKey?: BrpRouteKey | null;
    durationMonths: number | null;
    recommendationGroupId?: string | null;
    recommendationIds?: string[];
    comfort?: number;
    cost?: string | number;
    icon?: string | null;
    defaultIcon?: string | null;
    included?: boolean;
}

export interface PassportPlanStep {
    _key: string;
    stage: number;
    year: number | null;
    measures: PassportPlanMeasure[];
    isTemporary?: boolean;
}

export interface PassportSelectedStrategy {
    id: string;
    name: string;
}

const CUSTOM_STAGE_KEY_PREFIX = "custom:stage";
type NormalizePlanOptions = {
    preserveTemporaryEmptyStep?: boolean;
};

function getTotalPlanMeasures(steps: PassportPlanStep[]) {
    return steps.reduce((sum, step) => sum + (step.measures?.length ?? 0), 0);
}

function isPlanStepEmpty(step: PassportPlanStep) {
    return (step.measures?.length ?? 0) === 0;
}

function isTemporaryEmptyPlanStep(step: PassportPlanStep) {
    return !!step.isTemporary && isPlanStepEmpty(step);
}

function getNonEmptyPlanStepCount(steps: PassportPlanStep[]) {
    return steps.filter((step) => !isPlanStepEmpty(step)).length;
}

function hasTemporaryEmptyPlanStep(steps: PassportPlanStep[]) {
    return steps.some(isTemporaryEmptyPlanStep);
}

function hasRoomForTemporaryEmptyStep(steps: PassportPlanStep[]) {
    return getTotalPlanMeasures(steps) > getNonEmptyPlanStepCount(steps);
}

function clampInsertIndex(index: number, length: number) {
    return Math.max(0, Math.min(index, length));
}

function getNormalizePlanOptions(steps: PassportPlanStep[]): NormalizePlanOptions {
    return hasTemporaryEmptyPlanStep(steps)
        ? { preserveTemporaryEmptyStep: true }
        : {};
}

function normalizePlanStepSequence(
    steps: PassportPlanStep[],
    options: NormalizePlanOptions = {}
) {
    const { preserveTemporaryEmptyStep = false } = options;
    const temporaryEmptyStep =
        preserveTemporaryEmptyStep && hasRoomForTemporaryEmptyStep(steps)
            ? steps.find(isTemporaryEmptyPlanStep)
            : undefined;

    const normalizedSteps = steps
        .filter((step) => !isPlanStepEmpty(step) || step === temporaryEmptyStep)
        .map((step) =>
            step === temporaryEmptyStep
                ? {
                    ...step,
                    isTemporary: true,
                    measures: []
                }
                : {
                    ...step,
                    isTemporary: false
                }
        );

    return normalizedSteps.map((step, index) => ({
        ...step,
        stage: index + 1
    }));
}

function createTemporaryPlanStep(): PassportPlanStep {
    return {
        _key: `${CUSTOM_STAGE_KEY_PREFIX}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
        stage: 0,
        year: null,
        measures: [],
        isTemporary: true
    };
}

function assignStepSlotYears(steps: PassportPlanStep[], slotYears: Array<number | null>) {
    return steps.map((step, index) => ({
        ...step,
        year: slotYears[index] ?? null
    }));
}

function getRelevantRoadmapGroupIds(steps: PassportStep[]) {
    return new Set(
        steps.flatMap((step) =>
            (step.items ?? [])
                .map((item) => item.recommendationGroupId)
                .filter((id): id is string => !!id)
        )
    );
}

function getRelevantSelectedRecommendationIds(
    steps: PassportStep[],
    recommendationGroups: RecommendationGroup[]
) {
    const relevantGroupIds = getRelevantRoadmapGroupIds(steps);

    return Array.from(
        new Set(
            getSelectedRecommendationsId(
                recommendationGroups.filter((group) => relevantGroupIds.has(group.id))
            )
        )
    );
}

function base64ToBlob(base64: string, contentType: string) {
    const normalized = base64.includes(",") ? base64.split(",").pop() ?? "" : base64;
    const binary = window.atob(normalized);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
    }

    return new Blob([bytes], { type: contentType });
}

function downloadPdfFile(blob: Blob, filename: string) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(objectUrl);
}

interface BrPassportState {
    selectedYears: number[];
    selectedGoal: string;
    selectedStrategy: PassportSelectedStrategy | undefined;
    planYearSteps: PassportPlanStep[];
    currentStepIndex: number;
    formComplete: boolean;
    upgradedYears: Record<string, number | undefined>;
    roadmap: RoadmapStrategy[];
    generatedResponse?: BrpResponse;
    generatedPdf?: {
        base64: string;
        filename: string;
        contentType: string;
    };
    isGeneratingReport: boolean;
    roadmapSelectionSnapshot: string[];
}

export const usePassport = defineStore("passport", {
    state: (): BrPassportState => ({
        selectedYears: [],
        selectedGoal: "",
        selectedStrategy: undefined,
        planYearSteps: [],
        currentStepIndex: 0,
        formComplete: false,
        upgradedYears: {},
        roadmap: [],
        generatedResponse: undefined,
        generatedPdf: undefined,
        isGeneratingReport: false,
        roadmapSelectionSnapshot: []
    }),

    getters: {
        steps(): PassportStep[] {
            return this.planYearSteps.map((step, idx) => ({
                step: idx + 1,
                year: step.year ?? undefined,
                items: step.measures.map((measure) => ({
                    title: measure.name,
                    comfort: measure.comfort,
                    cost: measure.cost ?? "",
                    id: measure._key,
                    recommendations: measure.recommendationIds ?? [],
                    recommendationGroupId: measure.recommendationGroupId ?? null,
                    description: measure.description
                }))
            }));
        },

        response(): BrpResponse {
            if (this.generatedResponse) {
                return this.generatedResponse;
            }

            const res = {
                "status": "ok",
                "response": {
                    "building": {
                        "location": {
                            "lat": 39.458712,
                            "long": -0.391257
                        }
                    },
                    "plan": {
                        "country": "IT",
                        "CurrentYear": 2025,
                        "EnergyCost": "",
                        "YearlyEnergyCostVariation": null,
                        "YearlyMoneyValueVariation": null,
                        "YearlyECMCostVariation": null,
                        "userPreferences": {
                            "strategy": "IT.custom",
                            "strategyDetails": {
                                "objectiveVariable": "GlobalEPNRconsumption",
                                "objectiveValue": 0,
                                "objectiveYear": 2050,
                                "stages": 5,
                                "intervals=(stages)": {
                                    "stage0": {
                                        "number": 0,
                                        "start": 2025,
                                        "duration": 3,
                                        "scenarioApplied": null,
                                        "scenarioResulting": null
                                    },
                                    "stage1": {
                                        "number": 1,
                                        "start": 2028,
                                        "duration": 5,
                                        "scenarioApplied": null,
                                        "scenarioResulting": null
                                    },
                                    "stage2": {
                                        "number": 2,
                                        "start": 2033,
                                        "duration": 6,
                                        "scenarioApplied": null,
                                        "scenarioResulting": null
                                    },
                                    "stage3": {
                                        "number": 3,
                                        "start": 2039,
                                        "duration": 6,
                                        "scenarioApplied": null,
                                        "scenarioResulting": null
                                    },
                                    "stage4": {
                                        "number": 4,
                                        "start": 2045,
                                        "duration": 5,
                                        "scenarioApplied": null,
                                        "scenarioResulting": null
                                    }
                                }
                            }
                        }
                    },
                    "stages": {
                        "stage0": {
                            "number": 0,
                            "year": 2025,
                            "scenarioApplied": null,
                            "scenarioResulting": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 113.36,
                                            "LetterEPNRconsumption": "E",
                                            "Globaldemand": 68.77,
                                            "GlobalCO2emissions": 20.74,
                                            "LetterCO2emissions": "E"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 4200
                                        }
                                    }
                                ]
                            },
                            "calculatedKPIs": [
                                {
                                    "id_family": "ES.EPC",
                                    "KPIs": {
                                        "stage%GlobalEPNRconsumptionSavings": null,
                                        "stage%GlobaldemandSavings": null,
                                        "stage%GlobalCO2emissionsSavings": null,
                                        "stageNLetterEPNRconsumptionImprovement": null,
                                        "stageNLetterCO2emissionsImprovement": null,
                                        "stage%GlobalEPNRconsumptionContributionToZEB": null,
                                        "stage%GlobaldemandContributionToZEB": null,
                                        "stage%GlobalCO2emissionsContributionToZEB": null,
                                        "accumulated%GlobalEPNRconsumptionContributionToZEB": null,
                                        "accumulated%GlobaldemandContributionToZEB": null,
                                        "accumulated%GlobalCO2emissionsContributionToZEB": null
                                    }
                                },
                                {
                                    "id_family": "ES.energyCost",
                                    "KPIs": {
                                        "energyBillGlobal": null,
                                        "stageEnergyBillGlobal_reduction": null,
                                        "stage%energyBillGlobal_reduction": null,
                                        "accumulatedEnergyBillGlobal_reduction": null,
                                        "accumulated%energyBillGlobal_reduction": null
                                    }
                                },
                                {
                                    "id_family": "ES.stepCost",
                                    "KPIs": {
                                        "maintenaceCost": null,
                                        "interventionCost": null,
                                        "stepCost": null
                                    }
                                },
                                {
                                    "id_family": "ES.stepTimeLimits",
                                    "KPIs": {
                                        "payback": null,
                                        "paybackWarning": null,
                                        "stepLifetime": null,
                                        "stepLifeTimeWarning": null,
                                        "buildingLifetime": null,
                                        "buildingLifetimeWarning": null
                                    }
                                }
                            ]
                        },
                        "stage1": {
                            "number": 1,
                            "year": 2028,
                            "scenarioApplied": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 96.33,
                                            "LetterEPNRconsumption": "E",
                                            "Globaldemand": 59.68,
                                            "GlobalCO2emissions": 17.86,
                                            "LetterCO2emissions": "E"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 3900
                                        }
                                    }
                                ]
                            },
                            "scenarioResulting": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 96.33,
                                            "LetterEPNRconsumption": "E",
                                            "Globaldemand": 59.68,
                                            "GlobalCO2emissions": 17.86,
                                            "LetterCO2emissions": "E"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 3600
                                        }
                                    }
                                ]
                            },
                            "calculatedKPIs": [
                                {
                                    "id_family": "ES.EPC",
                                    "KPIs": {
                                        "stage%GlobalEPNRconsumptionSavings": 0.15022935779816515,
                                        "stage%GlobaldemandSavings": 0.13217972953322665,
                                        "stage%GlobalCO2emissionsSavings": 0.13886210221793632,
                                        "stageNLetterEPNRconsumptionImprovement": 0,
                                        "stageNLetterCO2emissionsImprovement": 0,
                                        "stage%GlobalEPNRconsumptionContributionToZEB": 0.15022935779816515,
                                        "stage%GlobaldemandContributionToZEB": 0.13217972953322665,
                                        "stage%GlobalCO2emissionsContributionToZEB": 0.13886210221793632,
                                        "accumulated%GlobalEPNRconsumptionContributionToZEB": 0.15022935779816515,
                                        "accumulated%GlobaldemandContributionToZEB": 0.13217972953322665,
                                        "accumulated%GlobalCO2emissionsContributionToZEB": 0.13886210221793632
                                    }
                                },
                                {
                                    "id_family": "ES.energyCost",
                                    "KPIs": {
                                        "energyBillGlobal": 3600,
                                        "stageEnergyBillGlobal_reduction": 600,
                                        "stage%energyBillGlobal_reduction": 0.14285714285714285,
                                        "accumulatedEnergyBillGlobal_reduction": 600,
                                        "accumulated%energyBillGlobal_reduction": 0.14285714285714285
                                    }
                                },
                                {
                                    "id_family": "ES.stepCost",
                                    "KPIs": {
                                        "maintenaceCost": 12,
                                        "interventionCost": 45,
                                        "stepCost": 105
                                    }
                                },
                                {
                                    "id_family": "ES.stepTimeLimits",
                                    "KPIs": {
                                        "payback": 0.175,
                                        "paybackWarning": null,
                                        "stepLifetime": 7,
                                        "stepLifeTimeWarning": null,
                                        "buildingLifetime": 8,
                                        "buildingLifetimeWarning": null
                                    }
                                }
                            ]
                        },
                        "stage2": {
                            "number": 2,
                            "year": 2033,
                            "scenarioApplied": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 62.33,
                                            "LetterEPNRconsumption": "D",
                                            "Globaldemand": 41.09,
                                            "GlobalCO2emissions": 12.1,
                                            "LetterCO2emissions": "D"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 3400
                                        }
                                    }
                                ]
                            },
                            "scenarioResulting": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 51.05,
                                            "LetterEPNRconsumption": "C",
                                            "Globaldemand": 35.1,
                                            "GlobalCO2emissions": 10.74,
                                            "LetterCO2emissions": "D"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 3100
                                        }
                                    }
                                ]
                            },
                            "calculatedKPIs": [
                                {
                                    "id_family": "ES.EPC",
                                    "KPIs": {
                                        "stage%GlobalEPNRconsumptionSavings": 0.4700508668120004,
                                        "stage%GlobaldemandSavings": 0.41186327077747986,
                                        "stage%GlobalCO2emissionsSavings": 0.39865621500559906,
                                        "stageNLetterEPNRconsumptionImprovement": -2,
                                        "stageNLetterCO2emissionsImprovement": -1,
                                        "stage%GlobalEPNRconsumptionContributionToZEB": 0.35295338939063636,
                                        "stage%GlobaldemandContributionToZEB": 0.311494638069705,
                                        "stage%GlobalCO2emissionsContributionToZEB": 0.322508398656215,
                                        "accumulated%GlobalEPNRconsumptionContributionToZEB": 0.5496647847565279,
                                        "accumulated%GlobaldemandContributionToZEB": 0.48960302457466914,
                                        "accumulated%GlobalCO2emissionsContributionToZEB": 0.4821600771456123
                                    }
                                },
                                {
                                    "id_family": "ES.energyCost",
                                    "KPIs": {
                                        "energyBillGlobal": 3100,
                                        "stageEnergyBillGlobal_reduction": 500,
                                        "stage%energyBillGlobal_reduction": 0.1388888888888889,
                                        "accumulatedEnergyBillGlobal_reduction": 1100,
                                        "accumulated%energyBillGlobal_reduction": 0.2619047619047619
                                    }
                                },
                                {
                                    "id_family": "ES.stepCost",
                                    "KPIs": {
                                        "maintenaceCost": 16,
                                        "interventionCost": 40,
                                        "stepCost": 136
                                    }
                                },
                                {
                                    "id_family": "ES.stepTimeLimits",
                                    "KPIs": {
                                        "payback": 0.272,
                                        "paybackWarning": null,
                                        "stepLifetime": 6,
                                        "stepLifeTimeWarning": null,
                                        "buildingLifetime": 10,
                                        "buildingLifetimeWarning": null
                                    }
                                }
                            ]
                        },
                        "stage3": {
                            "number": 3,
                            "year": 2039,
                            "scenarioApplied": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 10.18,
                                            "LetterEPNRconsumption": "D",
                                            "Globaldemand": 68.77,
                                            "GlobalCO2emissions": 5.83,
                                            "LetterCO2emissions": "C"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 2900
                                        }
                                    }
                                ]
                            },
                            "scenarioResulting": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 17.69,
                                            "LetterEPNRconsumption": "B",
                                            "Globaldemand": 35.1,
                                            "GlobalCO2emissions": 3,
                                            "LetterCO2emissions": "B"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 2700
                                        }
                                    }
                                ]
                            },
                            "calculatedKPIs": [
                                {
                                    "id_family": "ES.EPC",
                                    "KPIs": {
                                        "stage%GlobalEPNRconsumptionSavings": 0.6534769833496572,
                                        "stage%GlobaldemandSavings": 0,
                                        "stage%GlobalCO2emissionsSavings": 0.7206703910614525,
                                        "stageNLetterEPNRconsumptionImprovement": -1,
                                        "stageNLetterCO2emissionsImprovement": -2,
                                        "stage%GlobalEPNRconsumptionContributionToZEB": 0.8005876591576885,
                                        "stage%GlobaldemandContributionToZEB": -0.959259259259259,
                                        "stage%GlobalCO2emissionsContributionToZEB": 0.45716945996275604,
                                        "accumulated%GlobalEPNRconsumptionContributionToZEB": 0.8439484827099506,
                                        "accumulated%GlobaldemandContributionToZEB": 0.48960302457466914,
                                        "accumulated%GlobalCO2emissionsContributionToZEB": 0.8553519768563163
                                    }
                                },
                                {
                                    "id_family": "ES.energyCost",
                                    "KPIs": {
                                        "energyBillGlobal": 2700,
                                        "stageEnergyBillGlobal_reduction": 400,
                                        "stage%energyBillGlobal_reduction": 0.12903225806451613,
                                        "accumulatedEnergyBillGlobal_reduction": 1500,
                                        "accumulated%energyBillGlobal_reduction": 0.35714285714285715
                                    }
                                },
                                {
                                    "id_family": "ES.stepCost",
                                    "KPIs": {
                                        "maintenaceCost": 10,
                                        "interventionCost": 72,
                                        "stepCost": 132
                                    }
                                },
                                {
                                    "id_family": "ES.stepTimeLimits",
                                    "KPIs": {
                                        "payback": 0.33,
                                        "paybackWarning": null,
                                        "stepLifetime": 4,
                                        "stepLifeTimeWarning": "Intervention won't last until next intervention",
                                        "buildingLifetime": 9,
                                        "buildingLifetimeWarning": null
                                    }
                                }
                            ]
                        },
                        "stage4": {
                            "number": 4,
                            "year": 2045,
                            "scenarioApplied": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 82.03,
                                            "LetterEPNRconsumption": "E",
                                            "Globaldemand": 68.89,
                                            "GlobalCO2emissions": 15.43,
                                            "LetterCO2emissions": "E"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 2500
                                        }
                                    }
                                ]
                            },
                            "scenarioResulting": {
                                "results": [
                                    {
                                        "id_family": "ES.EPC",
                                        "Parameters": {
                                            "GlobalEPNRconsumption": 0,
                                            "LetterEPNRconsumption": "A",
                                            "Globaldemand": 35.1,
                                            "GlobalCO2emissions": 0,
                                            "LetterCO2emissions": "A"
                                        }
                                    },
                                    {
                                        "id_family": "ES.energyCost",
                                        "Parameters": {
                                            "energyBillGlobal": 2300
                                        }
                                    }
                                ]
                            },
                            "calculatedKPIs": [
                                {
                                    "id_family": "ES.EPC",
                                    "KPIs": {
                                        "stage%GlobalEPNRconsumptionSavings": 1,
                                        "stage%GlobaldemandSavings": 0,
                                        "stage%GlobalCO2emissionsSavings": 1,
                                        "stageNLetterEPNRconsumptionImprovement": -1,
                                        "stageNLetterCO2emissionsImprovement": -1,
                                        "stage%GlobalEPNRconsumptionContributionToZEB": -3.6370830977953648,
                                        "stage%GlobaldemandContributionToZEB": -0.9626780626780627,
                                        "stage%GlobalCO2emissionsContributionToZEB": -4.1433333333333335,
                                        "accumulated%GlobalEPNRconsumptionContributionToZEB": 1,
                                        "accumulated%GlobaldemandContributionToZEB": 0.48960302457466914,
                                        "accumulated%GlobalCO2emissionsContributionToZEB": 1
                                    }
                                },
                                {
                                    "id_family": "ES.energyCost",
                                    "KPIs": {
                                        "energyBillGlobal": 2300,
                                        "stageEnergyBillGlobal_reduction": 400,
                                        "stage%energyBillGlobal_reduction": 0.14814814814814814,
                                        "accumulatedEnergyBillGlobal_reduction": 1900,
                                        "accumulated%energyBillGlobal_reduction": 0.4523809523809524
                                    }
                                },
                                {
                                    "id_family": "ES.stepCost",
                                    "KPIs": {
                                        "maintenaceCost": 18,
                                        "interventionCost": 50,
                                        "stepCost": 140
                                    }
                                },
                                {
                                    "id_family": "ES.stepTimeLimits",
                                    "KPIs": {
                                        "payback": 0.35,
                                        "paybackWarning": null,
                                        "stepLifetime": 5,
                                        "stepLifeTimeWarning": null,
                                        "buildingLifetime": 10,
                                        "buildingLifetimeWarning": null
                                    }
                                }
                            ]
                        }
                    }
                }
            };

            return res.response as unknown as BrpResponse;
        },
        recommendationsSelectionsChanged(): boolean {
            if (!this.formComplete || !this.steps.length) return false;

            const recommendationsStore = useRecommendations();
            const current = getRelevantSelectedRecommendationIds(
                this.steps,
                recommendationsStore.recommendationGroups
            );

            if (!this.roadmapSelectionSnapshot.length) return false;
            if (current.length !== this.roadmapSelectionSnapshot.length) return true;

            const snapshotSet = new Set(this.roadmapSelectionSnapshot);
            return current.some((id) => !snapshotSet.has(id));
        }
    },

    actions: {
        refreshRoadmapSelectionSnapshot() {
            const recommendationsStore = useRecommendations();
            this.roadmapSelectionSnapshot = getRelevantSelectedRecommendationIds(
                this.steps,
                recommendationsStore.recommendationGroups
            );
        },

        normalizePlanSteps(options: NormalizePlanOptions = {}) {
            // Keep stage ids contiguous because BRP report generation derives scenario ids from them.
            this.planYearSteps = normalizePlanStepSequence(this.planYearSteps, options);
            this.currentStepIndex = Math.min(
                this.currentStepIndex,
                Math.max(0, this.planYearSteps.length - 1)
            );
        },

        canAddPlanStep() {
            return (
                !hasTemporaryEmptyPlanStep(this.planYearSteps) &&
                hasRoomForTemporaryEmptyStep(this.planYearSteps)
            );
        },

        setPlanYearSteps(steps: PassportPlanStep[]) {
            this.planYearSteps = steps;
            this.currentStepIndex = Math.max(0, steps.length - 1);
            this.normalizePlanSteps();
        },

        updatePlanStepYear(index: number, year: number | null) {
            if (!this.planYearSteps[index]) return;
            this.planYearSteps[index].year = year;
        },

        movePlanMeasure(
            sourceStepIndex: number,
            sourceMeasureIndex: number,
            targetStepIndex: number,
            targetMeasureIndex?: number
        ) {
            const sourceStep = this.planYearSteps[sourceStepIndex];
            const targetStep = this.planYearSteps[targetStepIndex];
            const isSameStep = sourceStepIndex === targetStepIndex;
            const normalizeOptions = getNormalizePlanOptions(this.planYearSteps);

            if (!sourceStep || !targetStep) return;
            if (
                sourceMeasureIndex < 0 ||
                sourceMeasureIndex >= sourceStep.measures.length
            ) {
                return;
            }
            if (
                !canMovePlanMeasure(this.planYearSteps, {
                    sourceStepIndex,
                    sourceMeasureIndex,
                    targetStepIndex,
                    targetMeasureIndex
                })
            ) {
                return;
            }

            const [movedMeasure] = sourceStep.measures.splice(sourceMeasureIndex, 1);
            if (!movedMeasure) return;

            let insertIndex =
                typeof targetMeasureIndex === "number"
                    ? targetMeasureIndex
                    : targetStep.measures.length;

            if (
                isSameStep &&
                typeof targetMeasureIndex === "number" &&
                targetMeasureIndex > sourceMeasureIndex
            ) {
                insertIndex -= 1;
            }

            insertIndex = clampInsertIndex(insertIndex, targetStep.measures.length);
            targetStep.measures.splice(insertIndex, 0, movedMeasure);
            this.normalizePlanSteps(normalizeOptions);
        },

        movePlanStep(sourceStepIndex: number, targetStepIndex: number) {
            if (
                sourceStepIndex < 0 ||
                targetStepIndex < 0 ||
                sourceStepIndex >= this.planYearSteps.length ||
                targetStepIndex > this.planYearSteps.length ||
                sourceStepIndex === targetStepIndex
            ) {
                return;
            }
            if (
                !canMovePlanStep(this.planYearSteps, {
                    sourceStepIndex,
                    targetStepIndex
                })
            ) {
                return;
            }

            const normalizeOptions = getNormalizePlanOptions(this.planYearSteps);
            const slotYears = this.planYearSteps.map((step) => step.year ?? null);
            const reorderedSteps = [...this.planYearSteps];
            const [movedStep] = reorderedSteps.splice(sourceStepIndex, 1);
            if (!movedStep) return;

            let insertIndex = targetStepIndex;
            insertIndex = clampInsertIndex(insertIndex, reorderedSteps.length);
            reorderedSteps.splice(insertIndex, 0, movedStep);
            this.planYearSteps = assignStepSlotYears(reorderedSteps, slotYears);
            this.normalizePlanSteps(normalizeOptions);
        },

        addPlanStep() {
            if (!this.canAddPlanStep()) return;

            this.planYearSteps = [
                ...this.planYearSteps,
                createTemporaryPlanStep()
            ];
            this.normalizePlanSteps({
                preserveTemporaryEmptyStep: true
            });
        },

        removeTemporaryPlanStep(index: number) {
            const step = this.planYearSteps[index];
            if (!step || !isTemporaryEmptyPlanStep(step)) return;

            this.planYearSteps.splice(index, 1);
            this.normalizePlanSteps();
        },

        setSelectedStrategy(strategy?: PassportSelectedStrategy) {
            this.selectedStrategy = strategy;
        },

        resetPlanYearSteps() {
            this.planYearSteps = [];
            this.currentStepIndex = 0;
        },

        setSelectedGoal(goal: string) {
            this.selectedGoal = goal;
            this.resetPlanYearSteps();
        },

        setFormComplete(value: boolean) {
            this.formComplete = value;
        },

        async generateOcrReport() {
            const recommendationsStore = useRecommendations();
            const loanStore = useLoan();
            const selectionsStore = useSelections();

            this.normalizePlanSteps();

            if (!this.planYearSteps.length || !this.selectedStrategy?.id) {
                throw new Error("Missing BRP plan data");
            }

            this.isGeneratingReport = true;

            try {
                const result = await BrpReportService.generate({
                    berwowId: recommendationsStore.berwowId,
                    recommendationGroups: recommendationsStore.recommendationGroups,
                    planYearSteps: this.planYearSteps,
                    roadmap: this.roadmap,
                    selectedStrategyId: this.selectedStrategy.id,
                    loanOptions: loanStore.loanOptions.slice(1),
                    selectedLoan: loanStore.selectedLoan,
                    propertyValue: recommendationsStore.report?.propertyValue,
                    mortgageActive: loanStore.mortgageActive,
                    mortgage: loanStore.mortgage,
                    addressInfo: selectionsStore.addressInfo,
                    addressResult: selectionsStore.addressResult,
                    berDetails: selectionsStore.berData?.berDetails,
                    houseTypology: selectionsStore.houseTypologySelected,
                    storeys: selectionsStore.storeys,
                    age: selectionsStore.age
                });

                if (result.pdfResponse?.base64) {
                    this.generatedPdf = {
                        base64: result.pdfResponse.base64,
                        filename: result.pdfResponse.filename || "home-upgrade-roadmap.pdf",
                        contentType: result.pdfResponse.contentType || "application/pdf"
                    };
                }

                return result;
            } finally {
                this.isGeneratingReport = false;
            }
        },

        async finalizePassport() {
            const result = await this.generateOcrReport();
            this.refreshRoadmapSelectionSnapshot();
            this.setFormComplete(true);
            this.downloadGeneratedPdf();
            return result;
        },

        downloadGeneratedPdf() {
            if (!this.generatedPdf?.base64) {
                throw new Error("No generated BRP PDF available");
            }

            downloadPdfFile(
                base64ToBlob(
                    this.generatedPdf.base64,
                    this.generatedPdf.contentType || "application/pdf"
                ),
                this.generatedPdf.filename || "home-upgrade-roadmap.pdf"
            );
        },

        clear() {
            this.selectedGoal = "";
            this.selectedStrategy = undefined;
            this.selectedYears = [];
            this.formComplete = false;
            this.generatedResponse = undefined;
            this.generatedPdf = undefined;
            this.isGeneratingReport = false;
            this.roadmapSelectionSnapshot = [];
            this.resetPlanYearSteps();
            this.roadmap = [];
            Object.keys(this.upgradedYears).forEach((k) => delete this.upgradedYears[k]);
        }
    }
});
